import { Box } from "@mui/material";
import { useId } from "react";
import {
    BOARD_ENGRAVED_GROOVE_FILTER_BASE,
    BOARD_ENGRAVED_GROOVE_STROKE,
    BoardEngravedGrooveFilter,
} from "./BoardEngravedGrooveFilter";
import {
    PUZZLE_3D_BEVEL_FILTER_BASE,
    PUZZLE_3D_DIE_STROKE,
    Puzzle3DBevelFilter,
} from "./Puzzle3DBevelFilter";

const SVG_SIZE = 1536;

const overlayBaseSx = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    zIndex: 0,
    display: "block",
};

const imgFallbackSx = {
    ...overlayBaseSx,
    objectFit: "contain",
};

/**
 * Die lines: stroke each piece outline in board SVG space (0–1536).
 * Falls back to full-board svg image when `pieces` is missing (older payloads).
 * @param {boolean} [bevel3d=false] — raised cardboard cut via puzzle-3d-bevel filter
 * @param {boolean} [engravedGroove=false] — inset board grooves via board-engraved-groove filter
 * @param {number} [zIndex=0] — stack order; use > piece z-index (e.g. 2) to draw lines on top of tray pieces.
 * @param {number} [strokeWidth=1.35] — die stroke width in SVG user units (0–1536 space).
 * @param {string} [preserveAspectRatio="xMidYMid meet"] — align 1536×1536 die art with contained image.
 */
export function JigsawDieLineOverlay({
    pieces,
    opacity = 0.6,
    svgPathFallback,
    zIndex = 0,
    strokeWidth = 1.35,
    preserveAspectRatio = "xMidYMid meet",
    bevel3d = false,
    engravedGroove = false,
    grooveShadowOpacity,
    grooveHighlightOpacity,
    grooveStroke,
}) {
    const reactId = useId().replace(/:/g, "");
    const useGroove = engravedGroove;
    const useBevel = bevel3d && !engravedGroove;
    const filterId = useGroove
        ? `${BOARD_ENGRAVED_GROOVE_FILTER_BASE}-${reactId}`
        : useBevel
          ? `${PUZZLE_3D_BEVEL_FILTER_BASE}-${reactId}`
          : null;

    const list = Array.isArray(pieces) ? pieces : [];
    if (list.length === 0) {
        if (!svgPathFallback) return null;
        return (
            <Box
                component="img"
                src={svgPathFallback}
                alt=""
                referrerPolicy="no-referrer"
                sx={{ ...imgFallbackSx, zIndex, opacity }}
            />
        );
    }

    const stroke = useGroove
        ? (grooveStroke ?? BOARD_ENGRAVED_GROOVE_STROKE)
        : useBevel
          ? PUZZLE_3D_DIE_STROKE
          : "rgba(255,255,255,0.92)";

    return (
        <Box
            component="svg"
            viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
            preserveAspectRatio={preserveAspectRatio}
            sx={{ ...overlayBaseSx, zIndex, opacity }}
            aria-hidden
        >
            {filterId ? (
                <defs>
                    {useGroove ? (
                        <BoardEngravedGrooveFilter
                            filterId={filterId}
                            shadowOpacity={grooveShadowOpacity}
                            highlightOpacity={grooveHighlightOpacity}
                        />
                    ) : null}
                    {useBevel ? <Puzzle3DBevelFilter filterId={filterId} /> : null}
                </defs>
            ) : null}
            {list.map((p, i) => (
                <path
                    key={p.index ?? i}
                    d={p.path}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="nonScalingStroke"
                    filter={filterId ? `url(#${filterId})` : undefined}
                />
            ))}
        </Box>
    );
}

export default JigsawDieLineOverlay;
